import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API, authFetch } from "@/App";
import { 
  ArrowLeft, Users, FolderKanban, TrendingUp, Calendar,
  Target, CheckCircle, Clock, PauseCircle, BarChart3,
  Activity, Award, Zap, FileText, Globe, UserPlus, Eye,
  MousePointerClick, ArrowUpRight
} from "lucide-react";

export const AdvancedAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const headers = token ? { "Authorization": `Bearer ${token}` } : {};
      const response = await authFetch(`${API}/consultant/analytics`, { 
        
        headers 
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        console.error("Analytics fetch failed:", response.status);
      }
    } catch (_error) {
      console.error("Error fetching analytics:", _error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <BarChart3 className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-600">Unable to load analytics</p>
          <Button onClick={() => navigate("/consultant")} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const { overview, client_tiers, traffic, leads, projects, rapid_framework, engagement, meetings, workshops, action_plans, top_clients } = analytics;

  // Chart-like visualization for project types
  const projectTypeColors = {
    "Business Setup": "bg-blue-500",
    "Graphic Design": "bg-purple-500",
    "SOP Creation": "bg-green-500",
    "Admin Support": "bg-orange-500",
    "Social Media": "bg-pink-500",
    "SEO": "bg-cyan-500",
    "Marketing": "bg-red-500",
    "Media Promo": "bg-yellow-500",
    "Other": "bg-gray-500"
  };

  const rapidStatusLabels = {
    "intake_pending": "Intake Pending",
    "intake_complete": "Intake Complete",
    "session_1": "Session 1",
    "session_2": "Session 2",
    "session_3": "Session 3",
    "momentum": "Momentum Phase",
    "completed": "Completed"
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/consultant")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">Advanced Analytics</h1>
                <p className="text-sm text-neutral-500">Comprehensive business metrics and insights</p>
              </div>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">
              <Activity className="w-3 h-3 mr-1" />
              Live Data
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card className="border-neutral-200 bg-gradient-to-br from-cyan-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Eye className="w-6 h-6 text-cyan-600" />
                <span className="text-xs font-medium text-cyan-600 bg-cyan-100 px-2 py-0.5 rounded-full">
                  {traffic?.views_today || 0} today
                </span>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{traffic?.views_30d || 0}</p>
              <p className="text-sm text-neutral-500">Page Views (30d)</p>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 bg-gradient-to-br from-rose-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <UserPlus className="w-6 h-6 text-rose-600" />
                <span className="text-xs font-medium text-rose-600 bg-rose-100 px-2 py-0.5 rounded-full">
                  {leads?.assessment_leads?.last_7d || 0} this week
                </span>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{leads?.total_all_leads || 0}</p>
              <p className="text-sm text-neutral-500">Total Leads</p>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-6 h-6 text-emerald-600" />
                <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                  {overview.active_clients_30d} active
                </span>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{overview.total_clients}</p>
              <p className="text-sm text-neutral-500">Total Clients</p>
            </CardContent>
          </Card>
          
          <Card className="border-neutral-200 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <FolderKanban className="w-6 h-6 text-blue-600" />
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                  {projects.active} active
                </span>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{projects.total}</p>
              <p className="text-sm text-neutral-500">Total Projects</p>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-6 h-6 text-purple-600" />
                <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                  {rapid_framework.by_status?.completed || 0} done
                </span>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{rapid_framework.total_enrollments}</p>
              <p className="text-sm text-neutral-500">R.A.P.I.D. Enrollments</p>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <FileText className="w-6 h-6 text-amber-600" />
                <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                  {engagement.recent_logs_30d} recent
                </span>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{engagement.total_weekly_logs}</p>
              <p className="text-sm text-neutral-500">Weekly Logs</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="traffic" className="space-y-6">
          <TabsList className="grid grid-cols-6 w-full max-w-2xl">
            <TabsTrigger value="traffic">Traffic</TabsTrigger>
            <TabsTrigger value="leads">Leads</TabsTrigger>
            <TabsTrigger value="clients">Clients</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
            <TabsTrigger value="rapid">R.A.P.I.D.</TabsTrigger>
            <TabsTrigger value="engagement">Engagement</TabsTrigger>
          </TabsList>

          {/* Traffic Tab */}
          <TabsContent value="traffic" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center"><Eye className="w-5 h-5 mr-2 text-cyan-600" />Traffic Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {[
                      { label: "Today", value: traffic?.views_today || 0, color: "cyan" },
                      { label: "7 Days", value: traffic?.views_7d || 0, color: "blue" },
                      { label: "30 Days", value: traffic?.views_30d || 0, color: "indigo" },
                      { label: "Unique Visitors", value: traffic?.unique_visitors_30d || 0, color: "purple" }
                    ].map((s, i) => (
                      <div key={i} className="text-center p-3 bg-neutral-50 rounded-lg">
                        <p className="text-2xl font-bold text-neutral-900">{s.value}</p>
                        <p className="text-xs text-neutral-500">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center"><Globe className="w-5 h-5 mr-2 text-blue-600" />Top Pages (30d)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(traffic?.top_pages || []).map((p, i) => {
                      const maxViews = Math.max(...(traffic?.top_pages || []).map(x => x.views), 1);
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-xs font-mono text-neutral-600 truncate w-40">{p.page}</span>
                          <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                            <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${(p.views / maxViews) * 100}%` }} />
                          </div>
                          <span className="text-xs font-medium text-neutral-700 w-8 text-right">{p.views}</span>
                        </div>
                      );
                    })}
                    {(!traffic?.top_pages || traffic.top_pages.length === 0) && (
                      <p className="text-sm text-neutral-400 text-center py-4">Traffic data will appear as visitors browse the site</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Daily Trend */}
            {traffic?.daily_trend?.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center"><TrendingUp className="w-5 h-5 mr-2 text-emerald-600" />Daily Page Views (14d)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-1 h-32">
                    {(traffic?.daily_trend || []).map((d, i) => {
                      const maxVal = Math.max(...(traffic?.daily_trend || []).map(x => x.views), 1);
                      const height = (d.views / maxVal) * 100;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-[10px] text-neutral-500">{d.views}</span>
                          <div className="w-full bg-cyan-500 rounded-t" style={{ height: `${Math.max(height, 4)}%` }} />
                          <span className="text-[8px] text-neutral-400 -rotate-45">{d.date?.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads" className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center"><UserPlus className="w-5 h-5 mr-2 text-rose-600" />Assessment Leads</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-neutral-600">Total Leads</span>
                      <span className="text-lg font-bold">{leads?.assessment_leads?.total || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-neutral-600">New</span>
                      <Badge className="bg-blue-100 text-blue-700">{leads?.assessment_leads?.new || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-neutral-600">Contacted</span>
                      <Badge className="bg-amber-100 text-amber-700">{leads?.assessment_leads?.contacted || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-neutral-600">Converted</span>
                      <Badge className="bg-emerald-100 text-emerald-700">{leads?.assessment_leads?.converted || 0}</Badge>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Conversion Rate</span>
                        <span className="text-lg font-bold text-emerald-600">{leads?.assessment_leads?.conversion_rate || 0}%</span>
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <p className="text-xs text-neutral-500 mb-2">By Clarity Score</p>
                      <div className="flex gap-2">
                        <div className="flex-1 text-center p-2 bg-emerald-50 rounded"><p className="text-sm font-bold text-emerald-700">{leads?.assessment_leads?.by_score?.high || 0}</p><p className="text-[10px] text-neutral-500">High (70+)</p></div>
                        <div className="flex-1 text-center p-2 bg-amber-50 rounded"><p className="text-sm font-bold text-amber-700">{leads?.assessment_leads?.by_score?.mid || 0}</p><p className="text-[10px] text-neutral-500">Mid (40-69)</p></div>
                        <div className="flex-1 text-center p-2 bg-red-50 rounded"><p className="text-sm font-bold text-red-700">{leads?.assessment_leads?.by_score?.low || 0}</p><p className="text-[10px] text-neutral-500">Low (&lt;40)</p></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center"><FileText className="w-5 h-5 mr-2 text-purple-600" />Other Lead Sources</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { label: "Service Quote Requests", value: leads?.service_quotes?.total || 0, new_val: leads?.service_quotes?.new || 0, color: "purple" },
                      { label: "Partnership Inquiries", value: leads?.partnership_inquiries?.total || 0, month: leads?.partnership_inquiries?.last_30d || 0, color: "blue" },
                      { label: "Project Scope Requests", value: leads?.project_scopes?.total || 0, month: leads?.project_scopes?.last_30d || 0, color: "indigo" },
                      { label: "Community Sign-ups", value: leads?.community_signups?.total || 0, month: leads?.community_signups?.last_30d || 0, color: "emerald" }
                    ].map((src, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
                        <span className="text-sm text-neutral-600">{src.label}</span>
                        <div className="text-right">
                          <span className="text-sm font-bold">{src.value}</span>
                          {src.new_val > 0 && <span className="text-xs text-rose-500 ml-1">({src.new_val} new)</span>}
                          {src.month > 0 && <span className="text-xs text-neutral-400 ml-1">({src.month} this month)</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center"><TrendingUp className="w-5 h-5 mr-2 text-emerald-600" />Lead Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(leads?.monthly_trend || []).map((m, i) => {
                      const maxLeads = Math.max(...(leads?.monthly_trend || []).map(x => x.leads), 1);
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-xs text-neutral-600 w-16">{m.month}</span>
                          <div className="flex-1 h-3 bg-neutral-100 rounded-full overflow-hidden">
                            <div className="h-full bg-rose-500 rounded-full" style={{ width: `${(m.leads / maxLeads) * 100}%` }} />
                          </div>
                          <span className="text-xs font-medium w-6 text-right">{m.leads}</span>
                        </div>
                      );
                    })}
                    {(!leads?.monthly_trend || leads.monthly_trend.length === 0) && (
                      <p className="text-sm text-neutral-400 text-center py-4">Lead data will appear as assessments are completed</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Client Tiers */}
              <Card className="border-neutral-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center">
                    <Award className="w-5 h-5 mr-2 text-emerald-600" />
                    Client Tiers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-neutral-600">R.A.P.I.D.™</span>
                        <span className="font-medium">{client_tiers.premium_rapid || 0}</span>
                      </div>
                      <Progress 
                        value={overview.total_clients > 0 ? ((client_tiers.premium_rapid || 0) / overview.total_clients) * 100 : 0} 
                        className="h-2 [&>div]:bg-purple-500" 
                      />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-neutral-600">Growth Paths</span>
                        <span className="font-medium">{client_tiers.growth_paths?.total || 0}</span>
                      </div>
                      <Progress 
                        value={overview.total_clients > 0 ? ((client_tiers.growth_paths?.total || 0) / overview.total_clients) * 100 : 0} 
                        className="h-2 [&>div]:bg-emerald-500" 
                      />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-neutral-600">Basic Consulting</span>
                        <span className="font-medium">{client_tiers.basic || 0}</span>
                      </div>
                      <Progress 
                        value={overview.total_clients > 0 ? ((client_tiers.basic || 0) / overview.total_clients) * 100 : 0} 
                        className="h-2 [&>div]:bg-blue-500" 
                      />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-neutral-600">Unassigned</span>
                        <span className="font-medium">{client_tiers.unassigned}</span>
                      </div>
                      <Progress 
                        value={overview.total_clients > 0 ? (client_tiers.unassigned / overview.total_clients) * 100 : 0} 
                        className="h-2 [&>div]:bg-gray-400" 
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Onboarding Status */}
              <Card className="border-neutral-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                    Onboarding Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center space-x-8 py-4">
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-2">
                        <span className="text-2xl font-bold text-green-700">{overview.onboarded_clients}</span>
                      </div>
                      <p className="text-sm text-neutral-600">Onboarded</p>
                    </div>
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mb-2">
                        <span className="text-2xl font-bold text-amber-700">{overview.pending_clients}</span>
                      </div>
                      <p className="text-sm text-neutral-600">Pending</p>
                    </div>
                  </div>
                  <div className="text-center text-sm text-neutral-500 mt-2">
                    {overview.total_clients > 0 
                      ? `${Math.round((overview.onboarded_clients / overview.total_clients) * 100)}% onboarding rate`
                      : "No clients yet"}
                  </div>
                </CardContent>
              </Card>

              {/* Top Clients */}
              <Card className="border-neutral-200 md:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-emerald-600" />
                    Top Engaged Clients
                  </CardTitle>
                  <CardDescription>Based on weekly log submissions</CardDescription>
                </CardHeader>
                <CardContent>
                  {top_clients.length === 0 ? (
                    <p className="text-neutral-500 text-center py-4">No engagement data yet</p>
                  ) : (
                    <div className="space-y-3">
                      {top_clients.map((client, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              idx === 0 ? "bg-yellow-100 text-yellow-700" :
                              idx === 1 ? "bg-gray-200 text-gray-700" :
                              idx === 2 ? "bg-orange-100 text-orange-700" :
                              "bg-neutral-100 text-neutral-600"
                            }`}>
                              <span className="font-semibold text-sm">#{idx + 1}</span>
                            </div>
                            <div>
                              <p className="font-medium text-neutral-900">{client.name}</p>
                              <p className="text-xs text-neutral-500">{client.email}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-emerald-700 border-emerald-200">
                            {client.log_count} logs
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Project Status */}
              <Card className="border-neutral-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Project Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center">
                        <Clock className="w-5 h-5 text-green-600 mr-2" />
                        <span className="text-neutral-700">Active</span>
                      </div>
                      <span className="text-2xl font-bold text-green-700">{projects.active}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <div className="flex items-center">
                        <PauseCircle className="w-5 h-5 text-yellow-600 mr-2" />
                        <span className="text-neutral-700">On Hold</span>
                      </div>
                      <span className="text-2xl font-bold text-yellow-700">{projects.on_hold}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
                        <span className="text-neutral-700">Completed</span>
                      </div>
                      <span className="text-2xl font-bold text-blue-700">{projects.completed}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Average Completion */}
              <Card className="border-neutral-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Avg. Project Progress</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center justify-center py-6">
                  <div className="relative w-32 h-32">
                    <svg className="w-32 h-32 transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth="12"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#059669"
                        strokeWidth="12"
                        strokeDasharray={`${(projects.avg_completion / 100) * 351.86} 351.86`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-bold text-neutral-900">{projects.avg_completion}%</span>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-500 mt-3">Active projects average</p>
                </CardContent>
              </Card>

              {/* Projects by Type */}
              <Card className="border-neutral-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">By Project Type</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(projects.by_type).length === 0 ? (
                    <p className="text-neutral-500 text-center py-4">No projects yet</p>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(projects.by_type)
                        .sort(([,a], [,b]) => b - a)
                        .slice(0, 6)
                        .map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className={`w-3 h-3 rounded-full mr-2 ${projectTypeColors[type] || "bg-gray-500"}`}></div>
                            <span className="text-sm text-neutral-700">{type}</span>
                          </div>
                          <span className="text-sm font-medium text-neutral-900">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* R.A.P.I.D. Tab */}
          <TabsContent value="rapid" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* R.A.P.I.D. Pipeline */}
              <Card className="border-neutral-200 md:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center">
                    <Target className="w-5 h-5 mr-2 text-purple-600" />
                    R.A.P.I.D. Framework Pipeline
                  </CardTitle>
                  <CardDescription>Client journey through the 6-phase framework</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-2">
                    {["intake_pending", "intake_complete", "session_1", "session_2", "session_3", "momentum", "completed"].map((status, idx) => {
                      const count = rapid_framework.by_status?.[status] || 0;
                      const maxCount = Math.max(...Object.values(rapid_framework.by_status || {1: 1}), 1);
                      return (
                        <div key={status} className="text-center">
                          <div 
                            className={`mx-auto rounded-lg transition-all ${
                              idx === 6 ? "bg-green-500" : 
                              idx > 3 ? "bg-purple-500" : 
                              idx > 0 ? "bg-blue-500" : "bg-gray-400"
                            }`}
                            style={{ 
                              height: `${Math.max(20, (count / maxCount) * 100)}px`,
                              width: "100%",
                              minHeight: "20px"
                            }}
                          >
                            <span className="text-white font-bold text-sm flex items-center justify-center h-full">
                              {count}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-500 mt-2 leading-tight">
                            {rapidStatusLabels[status] || status}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* R.A.P.I.D. Summary */}
              <Card className="border-neutral-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Framework Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-600">Total Enrollments</span>
                      <span className="text-xl font-bold text-neutral-900">{rapid_framework.total_enrollments}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-600">Completed</span>
                      <span className="text-xl font-bold text-green-600">{rapid_framework.by_status?.completed || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-600">In Progress</span>
                      <span className="text-xl font-bold text-blue-600">
                        {rapid_framework.total_enrollments - (rapid_framework.by_status?.completed || 0) - (rapid_framework.by_status?.intake_pending || 0)}
                      </span>
                    </div>
                    {rapid_framework.total_enrollments > 0 && (
                      <div className="pt-3 border-t">
                        <p className="text-sm text-neutral-500">
                          Completion Rate: {Math.round(((rapid_framework.by_status?.completed || 0) / rapid_framework.total_enrollments) * 100)}%
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Action Plans */}
              <Card className="border-neutral-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center">
                    <Zap className="w-5 h-5 mr-2 text-amber-600" />
                    Action Plans
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    <p className="text-4xl font-bold text-neutral-900">{action_plans.total}</p>
                    <p className="text-sm text-neutral-500">Total Plans Created</p>
                  </div>
                  {Object.keys(action_plans.by_category).length > 0 && (
                    <div className="mt-4 space-y-2">
                      {Object.entries(action_plans.by_category).map(([cat, count]) => (
                        <div key={cat} className="flex justify-between text-sm">
                          <span className="text-neutral-600 capitalize">{cat}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Engagement Tab */}
          <TabsContent value="engagement" className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Monthly Trend */}
              <Card className="border-neutral-200 md:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-emerald-600" />
                    Weekly Logs Trend
                  </CardTitle>
                  <CardDescription>Last 6 months of client engagement</CardDescription>
                </CardHeader>
                <CardContent>
                  {engagement.monthly_trend.length === 0 ? (
                    <p className="text-neutral-500 text-center py-8">No engagement data yet</p>
                  ) : (
                    <div className="flex items-end justify-between h-40 px-2">
                      {engagement.monthly_trend.map((item, idx) => {
                        const maxLogs = Math.max(...engagement.monthly_trend.map(i => i.logs), 1);
                        const height = Math.max(10, (item.logs / maxLogs) * 100);
                        return (
                          <div key={item.month} className="flex flex-col items-center flex-1">
                            <span className="text-xs font-medium text-neutral-700 mb-1">{item.logs}</span>
                            <div 
                              className="w-full max-w-12 bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t-md transition-all"
                              style={{ height: `${height}%` }}
                            ></div>
                            <span className="text-xs text-neutral-500 mt-2">
                              {new Date(item.month + "-01").toLocaleDateString('en-US', { month: 'short' })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Meetings & Workshops */}
              <Card className="border-neutral-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                    Meetings & Events
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-neutral-600">Meetings</span>
                      <span className="text-xl font-bold text-blue-700">{meetings.total}</span>
                    </div>
                    <div className="flex text-xs text-neutral-500 space-x-3">
                      <span>{meetings.scheduled} scheduled</span>
                      <span>{meetings.completed} completed</span>
                    </div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-600">Workshops/Events</span>
                      <span className="text-xl font-bold text-purple-700">{workshops.total}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Activity Summary */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Activity Summary (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <p className="text-3xl font-bold text-emerald-700">{overview.active_clients_30d}</p>
                    <p className="text-sm text-neutral-600">Active Clients</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-700">{engagement.recent_logs_30d}</p>
                    <p className="text-sm text-neutral-600">Logs Submitted</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-3xl font-bold text-purple-700">{meetings.scheduled}</p>
                    <p className="text-sm text-neutral-600">Upcoming Meetings</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <p className="text-3xl font-bold text-amber-700">{projects.active}</p>
                    <p className="text-sm text-neutral-600">Active Projects</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdvancedAnalyticsDashboard;
